import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

const ICON_MAP = {
  waived: (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 2v2m0 16v2M4.93 4.93l1.41 1.41m11.32 11.32l1.41 1.41M2 12h2m16 0h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  ),
  discount: (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M20.59 13.41L13.41 20.59a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.83z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <circle cx="7" cy="7" r="1.5" fill="currentColor" />
    </svg>
  ),
  savings: (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 3l7 3v5.5c0 4.4-3 8.2-7 9.5-4-1.3-7-5.1-7-9.5V6l7-3z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
} as const;

/**
 * Preferred Employer Program — introduction + benefit cards.
 *
 * Card treatment mirrors PepBenefits: three soft gray tiles, rounded-square
 * icon wells (secondary color), and primary-colored value callouts.
 */
export function PreferredEmployerBenefits() {
  useRevealStyles();
  const gridRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="peb-benefits">
      <div className="peb-benefits-container">
        <div className="peb-benefits-header">
          <RichTextContent
            path="preferredemployerbenefits.benefits_heading"
            as="h2"
            className="peb-benefits-heading"
          />
          <div className="peb-benefits-heading-divider" aria-hidden="true" />
        </div>

        <div ref={gridRef} className="reveal-fade-up peb-benefits-grid">
          <article className="peb-benefit-card">
            <div className="peb-benefit-icon" aria-hidden="true">{ICON_MAP.waived}</div>
            <RichTextContent
              path="preferredemployerbenefits.benefit1_title"
              as="h3"
              className="peb-benefit-title"
            />
            <RichTextContent
              path="preferredemployerbenefits.benefit1_body"
              as="div"
              className="peb-benefit-value"
            />
          </article>

          <article className="peb-benefit-card">
            <div className="peb-benefit-icon" aria-hidden="true">{ICON_MAP.discount}</div>
            <RichTextContent
              path="preferredemployerbenefits.benefit2_title"
              as="h3"
              className="peb-benefit-title"
            />
            <RichTextContent
              path="preferredemployerbenefits.benefit2_body"
              as="div"
              className="peb-benefit-value"
            />
          </article>

          <article className="peb-benefit-card">
            <div className="peb-benefit-icon" aria-hidden="true">{ICON_MAP.savings}</div>
            <RichTextContent
              path="preferredemployerbenefits.benefit3_title"
              as="h3"
              className="peb-benefit-title"
            />
            <RichTextContent
              path="preferredemployerbenefits.benefit3_body"
              as="div"
              className="peb-benefit-value"
            />
          </article>
        </div>
      </div>

      <style>{`
        .peb-benefits {
          padding: 4rem 0 4.5rem;
          background: var(--vibe-color-background, #fff);
        }
        @media (min-width: 768px) {
          .peb-benefits { padding: 5.5rem 0 6rem; }
        }

        .peb-benefits-container {
          max-width: 72rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) { .peb-benefits-container { padding: 0 1.5rem; } }
        @media (min-width: 1024px) { .peb-benefits-container { padding: 0 2rem; } }

        .peb-benefits-header {
          text-align: center;
          max-width: 42rem;
          margin: 0 auto 3rem;
        }

        .peb-benefits-heading {
          font-family: var(--vibe-font-heading);
          font-size: 2rem;
          font-weight: 700;
          line-height: 1.15;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: var(--vibe-color-text);
          margin: 0 0 1rem;
        }
        @media (min-width: 768px) {
          .peb-benefits-heading { font-size: 2.5rem; }
        }
        .peb-benefits-heading p { margin: 0; }

        .peb-benefits-heading-divider {
          width: 48px;
          height: 2px;
          margin: 0 auto;
          background: var(--vibe-color-primary);
          opacity: 0.85;
        }

        .peb-benefits-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.25rem;
          max-width: 60rem;
          margin: 0 auto;
        }
        @media (min-width: 768px) {
          .peb-benefits-grid { grid-template-columns: repeat(3, 1fr); gap: 1.5rem; }
        }

        .peb-benefit-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 2rem 1.75rem;
          background: #f7f7f8;
          border: 1px solid #e8e8ea;
          border-radius: var(--vibe-radius-lg, 12px);
          transition: box-shadow 0.25s, transform 0.25s, border-color 0.25s;
        }
        .peb-benefit-card:hover {
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.06);
          transform: translateY(-3px);
          border-color: color-mix(in srgb, var(--vibe-color-primary) 30%, #e8e8ea);
        }

        .peb-benefit-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 56px;
          height: 56px;
          border-radius: 14px;
          background: color-mix(in srgb, var(--vibe-color-secondary) 12%, transparent);
          color: var(--vibe-color-secondary);
          margin-bottom: 1.5rem;
        }

        .peb-benefit-title {
          font-family: var(--vibe-font-heading);
          font-size: 1.125rem;
          font-weight: 600;
          line-height: 1.3;
          color: var(--vibe-color-text);
          margin: 0 0 0.5rem;
        }
        .peb-benefit-title p { margin: 0; }

        .peb-benefit-value {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          font-weight: 600;
          line-height: 1.5;
          color: var(--vibe-color-text);
        }
        .peb-benefit-value :where(p) {
          margin: 0;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PreferredEmployerBenefits',
  displayName: 'Preferred Employer — Overview & Benefits',
  tokens: {
    'preferredemployerbenefits.benefits_heading': {
      label: 'Benefits Heading',
      type: 'richtext',
      defaultValue: '<p>Program Benefits</p>',
    },
    'preferredemployerbenefits.benefit1_title': {
      label: 'Benefit 1 — Title',
      type: 'richtext',
      defaultValue: '<p>Waived Application Fee</p>',
    },
    'preferredemployerbenefits.benefit1_body': {
      label: 'Benefit 1 — Description',
      type: 'richtext',
      defaultValue: '<p>($60 Value)</p>',
    },
    'preferredemployerbenefits.benefit2_title': {
      label: 'Benefit 2 — Title',
      type: 'richtext',
      defaultValue: '<p>Waived Administration Fee</p>',
    },
    'preferredemployerbenefits.benefit2_body': {
      label: 'Benefit 2 — Description',
      type: 'richtext',
      defaultValue: '<p>($200 Value)</p>',
    },
    'preferredemployerbenefits.benefit3_title': {
      label: 'Benefit 3 — Title',
      type: 'richtext',
      defaultValue: '<p>Total Savings</p>',
    },
    'preferredemployerbenefits.benefit3_body': {
      label: 'Benefit 3 — Description',
      type: 'richtext',
      defaultValue: '<p>$260!</p>',
    },
  },
};

(PreferredEmployerBenefits as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default PreferredEmployerBenefits;
